/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';

import { CoachBrandLabel } from 'bundles/ai-course-coach/assets/coachBranding';
import { CdsDisclosure } from 'bundles/ai-course-coach/components/common';
import ProfileImageCA from 'bundles/compound-assessments/components/shared/ProfileImageCA';
import leeCoachEpicClient from 'bundles/epic/clients/leeCoach';
import RateAiFeedback from 'bundles/peer/components/ai-grading/RateAiFeedback';

import _t from 'i18n!nls/ondemand';

const styles = {
  textFeedbackRow: css`
    padding: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-50);
  `,
  textFeedbackWrapper: css`
    padding-left: var(--cds-spacing-200);
  `,
  textFeedbackContent: css`
    padding-top: var(--cds-spacing-100);
  `,
  disclosureHeader: css`
    display: flex;
    gap: 8px;
    align-items: center;
  `,
};

type Props = {
  children: React.ReactNode;
  creatorFullName: string;
  creatorPhotoUrl: string;
  creatorScore: string | undefined | null;
  isAnonymous: boolean;
  isAIGraded?: boolean;
  reviewId?: string;
  reviewPartId?: string;
};

const TextFeedbackRow = (props: Props) => {
  const { creatorFullName, creatorPhotoUrl, creatorScore, children, isAnonymous, isAIGraded, reviewId, reviewPartId } =
    props;

  if (isAIGraded) {
    const enableBrandUpdate = leeCoachEpicClient.get('enableBrandUpdate') ?? false;
    const ariaLabel = enableBrandUpdate ? _t('Coursera AI') : _t('Coach, AI powered assistant');
    const header = (
      <div css={styles.disclosureHeader}>{enableBrandUpdate ? <AIGenerateBrandedNewIcon /> : <CoachBrandLabel />}</div>
    );
    return (
      <CdsDisclosure header={header} aria-label={ariaLabel} collapsible={false}>
        {creatorScore && (
          <div className="body-2-text review-score">
            <FormattedMessage message={_t('Score: {creatorScore} points')} creatorScore={creatorScore} />
          </div>
        )}
        {children && (
          <div css={styles.textFeedbackContent} className="text-feedback-content">
            {children}
          </div>
        )}
        {reviewId && reviewPartId && <RateAiFeedback reviewId={reviewId} reviewPartId={reviewPartId} />}
      </CdsDisclosure>
    );
  }

  return (
    <div css={styles.textFeedbackRow} className="rc-TextFeedbackRow bgcolor-black-g1 horizontal-box">
      <ProfileImageCA
        profile={{
          fullName: creatorFullName,
          photoUrl: creatorPhotoUrl,
          isAnonymous,
        }}
        alt={_t('Photo of learner #{creatorFullName}', { creatorFullName })}
      />

      <div css={styles.textFeedbackWrapper} className="text-feedback-wrapper">
        <div className="body-2-text">{creatorFullName}</div>
        {creatorScore && (
          <div className="body-2-text review-score">
            <FormattedMessage message={_t('Score: {creatorScore} points')} creatorScore={creatorScore} />
          </div>
        )}
        {children && (
          <div css={styles.textFeedbackContent} className="text-feedback-content">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextFeedbackRow;
